import {
  type Guard,
  type BaseContext,
  type BaseEvent,
} from '@atlassian/state-machine/types';

import { type MessageDescriptor } from '@canvas/intl';

export type MacModifier = 'cmd' | 'shift' | 'option';
export type SequenceType = 'option' | 'combination';

interface MacSequence {
  modifiers: MacModifier[];
  characters: MessageDescriptor[];
  raw: string[];
}

export type NonMacModifier = 'ctrl' | 'shift' | 'alt';

interface NonMacSequence {
  modifiers: NonMacModifier[];
  characters: MessageDescriptor[];
  raw: string[];
}

export interface ShortcutSequence {
  macOS: MacSequence;
  other: NonMacSequence;
}

export type EmptyContext =
  | {
      type: 'empty';
    }
  | any;
export type EmptyEvent =
  | {
      type: 'empty';
    }
  | any;

export interface Shortcut<
  T,
  C extends BaseContext = EmptyContext,
  E extends BaseEvent = EmptyEvent
> {
  id: T;
  title: MessageDescriptor;
  type?: SequenceType;
  aliases?: string[];
  sequence: ShortcutSequence;
  /**
   * Indicates if this shortcut is not visible to the user
   * - an implicit shortcut part of the user experience.
   */
  hidden?: boolean;
  /**
   * Whether or not to prevent the browser's default behaviour when this shortcut is
   * pressed.
   */
  preventDefault?: boolean;
  /**
   * The subcategory of the shortcut. Used to group shortcuts in the shortcut
   * modal, e.g. "Mindmaps".
   */
  subcategory?: MessageDescriptor;

  /**
   * Function that must pass in order for the shortcut to fire.
   * @see board-machine.ts
   */
  condition?: Guard<C, E>;

  /**
   * Action to be triggered when this shortcut fires
   */
  action?: (context: C) => void;
}

export interface ShortcutFlattened {
  id: string;
  title: MessageDescriptor;
  sequence: string[];
  type: SequenceType;
  /**
   * Whether or not to prevent the browser's default behaviour when this shortcut is
   * pressed.
   */
  preventDefault?: boolean;
  /**
   * The subcategory of the shortcut. Used to group shortcuts in the shortcut
   * modal, e.g. "Mindmaps".
   */
  subcategory?: MessageDescriptor;
}

export type ShortcutFlattenedWithSubcategory = ShortcutFlattened &
  Required<Pick<ShortcutFlattened, 'subcategory'>>;

export type OperatingSystem = 'mac' | 'non-mac';
export type PrintKind = 'short' | 'verbose';

// Ordered according to Apple Human Interface Guidelines
// https://developer.apple.com/design/human-interface-guidelines/keyboards
export const ShortcutModifierKeys = [
  'Control',
  'Alt',
  'Shift',
  'Command',
  'Meta',
] as const;
export type ShortcutModifier = (typeof ShortcutModifierKeys)[number];

export const DOMModifierKeys = ['Meta', 'Shift', 'Control', 'Alt'] as const;
export type DOMModifier = (typeof DOMModifierKeys)[number];
